import {createContext, useContext, useState, useEffect, useCallback, ReactNode} from 'react'
import axios from 'axios'
import {useAuth} from './AuthContext'

interface Connection {
    id: string
    dbUrl: string
    dbUsername: string
    accountName: string
    apiUserName: string
    origamiAccountId: number
}

interface CreateAccountRequest {
    accountName: string
    apiAccountUsername: string
    apiAccountPassword: string
}

interface UpdateAccountRequest {
    id: string
    accountName: string
    apiAccountUsername: string
    apiAccountPassword?: string
}

interface AccountContextType {
    accounts: Connection[]
    selectedAccount: string
    selectedAccountConnection: Connection | null
    setSelectedAccount: (account: string) => void
    loading: boolean
    error: boolean
    refetchAccounts: () => Promise<void>
    user: ReturnType<typeof useAuth>['user']
    isAdminUser: boolean
    createAccount: (data: CreateAccountRequest) => Promise<void>
    updateAccount: (data: UpdateAccountRequest) => Promise<void>
    deleteAccount: (id: string) => Promise<void>
}

const AccountContext = createContext<AccountContextType | undefined>(undefined)

export function AccountProvider({children}: {children: ReactNode}) {
    const {user, isAdminUser} = useAuth()
    const [accounts, setAccounts] = useState<Connection[]>([])
    const [selectedAccount, setSelectedAccount] = useState<string>('')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const fetchAccounts = useCallback(async () => {
        setLoading(true)
        setError(false)
        try {
            const {data} = await axios.get<Connection[]>('/api/admin/accounts')
            setAccounts(data)
            setSelectedAccount(current => (data.length > 0 && !current ? data[0].accountName : current))
        } catch (e) {
            console.error('Failed to fetch accounts:', e)
            setError(true)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchAccounts()
    }, [fetchAccounts])

    const selectedAccountConnection = accounts.find(acc => acc.accountName === selectedAccount) || null

    const createAccount = useCallback(async (data: CreateAccountRequest) => {
        if (!isAdminUser) {
            throw new Error('Only admin users can create accounts')
        }
        
        await axios.post('/api/admin/create/account', data)
        await fetchAccounts()
    }, [isAdminUser, fetchAccounts])

    const updateAccount = useCallback(async (data: UpdateAccountRequest) => {
        if (!isAdminUser) {
            throw new Error('Only admin users can update accounts')
        }
        
        await axios.put('/api/admin/update/account', data)
        await fetchAccounts()
    }, [isAdminUser, fetchAccounts])

    const deleteAccount = useCallback(async (id: string) => {
        if (!isAdminUser) {
            throw new Error('Only admin users can delete accounts')
        }
        
        await axios.delete(`/api/admin/delete/account/${id}`)
        await fetchAccounts()
    }, [isAdminUser, fetchAccounts])

    return (
        <AccountContext.Provider value={{
            accounts,
            selectedAccount,
            selectedAccountConnection,
            setSelectedAccount,
            loading,
            error,
            refetchAccounts: fetchAccounts,
            user,
            isAdminUser,
            createAccount,
            updateAccount,
            deleteAccount,
        }}>
            {children}
        </AccountContext.Provider>
    )
}

export function useAccount() {
    const context = useContext(AccountContext)
    if (context === undefined) {
        throw new Error('useAccount must be used within an AccountProvider')
    }
    return context
}
